"use client";
import { useEffect, useState, useCallback } from "react";
import { getAuthStatus } from "@/lib/auth";
import { KeywordLabel } from "@/components/common/KeywordLabel";
import LoginRequiredToast from "@/components/common/LoginRequiredToast";

interface ReportCardProps {
  title: string;
  description: string;
  date: string;
  imageUrl: string;
  keywords?: string[];
  size?: "large" | "medium" | "small";
  href?: string;
  isCuration?: boolean; // 큐레이션 섹션 여부
}

let __authCache: boolean | null = null;

export function ReportCard({ 
  title, 
  description, 
  date, 
  imageUrl, 
  keywords = ["마케팅"], 
  size = "large",
  href,
  isCuration = false,
}: ReportCardProps) {
  const [isMobile, setIsMobile] = useState(false);
  
  // 화면 크기 감지 (모바일/PC만)
  const handleResize = useCallback(() => {
    const width = window.innerWidth;
    setIsMobile(width < 1024); // lg breakpoint
  }, []);
  
  // 화면 크기 감지 이벤트 리스너
  useEffect(() => {
    handleResize(); // 초기 설정
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [handleResize]);
  
  const sizeClasses = {
    large: {
      container: "w-full h-[240px] lg:h-[315px]",
      image: "h-[240px] lg:h-[315px]",
      titleText: "text-2xl lg:text-[26px]",
      titleHeight: isMobile ? "h-auto" : "h-auto lg:h-[78px]",
      descHeight: isMobile ? "h-auto" : "h-auto lg:h-[81px]",
      cardHeight: isMobile ? "h-auto" : "h-auto lg:h-[241px]",
      contentGap: isMobile ? "gap-2" : "gap-2",
      titleDescGap: isMobile ? "gap-2" : "gap-2",
    },
    medium: {
      container: "w-full h-[200px] lg:h-[230px]",
      image: "h-[200px] lg:h-[230px]",
      titleText: "text-2xl lg:text-[26px]",
      titleHeight: isMobile ? "h-auto" : "h-auto lg:h-[68px]",
      descHeight: isMobile ? "h-auto" : "h-auto lg:h-[54px]",
      cardHeight: isMobile ? "h-auto" : "h-auto lg:h-[102px]",
      contentGap: isMobile ? "gap-2" : "gap-2",
      titleDescGap: isMobile ? "gap-2" : "gap-2",
    },
    small: {
      container: "w-full h-36 lg:h-40",
      image: "h-36 lg:h-40",
      titleText: "text-lg lg:text-[24px]",
      titleHeight: isMobile ? "h-auto" : "h-auto lg:h-16",
      descHeight: isMobile ? "h-auto" : "h-auto lg:h-[54px]",
      cardHeight: "h-auto",
      contentGap: isMobile ? "gap-2" : "gap-2",
      titleDescGap: isMobile ? "gap-2" : "gap-2",
    },
  } as const;

  const classes = sizeClasses[size];

  // 로그인 여부는 /auth/me 호출로 판단(간단 캐시)
  const [isAuthed, setIsAuthed] = useState<boolean | null>(__authCache);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (__authCache !== null) return; // 캐시 사용
    let mounted = true;
    getAuthStatus()
      .then(d => {
        if (!mounted) return;
        const ok = !!(d?.ok && d?.user);
        __authCache = ok;
        setIsAuthed(ok);
      })
      .catch(() => { if (mounted) { __authCache = false; setIsAuthed(false); } });
    return () => { mounted = false; };
  }, []);

  const onBlockedClick: React.MouseEventHandler = (e) => {
    if (isAuthed !== false) { e.preventDefault(); return; }
    e.preventDefault();
    setToastVisible(true);
  };

  const [toastVisible, setToastVisible] = useState(false);

  return (
    <div className="content-stretch flex flex-col gap-4 items-start justify-start relative shrink-0 w-full">
      {href ? (
        isAuthed === true ? (
          <a href={href} className={`bg-center bg-cover bg-no-repeat rounded-[20px] shrink-0 ${classes.container}`} style={{ backgroundImage: `url('${imageUrl}')` }} aria-label={title} />
        ) : isAuthed === false ? (
          <a href="#" onClick={onBlockedClick} className={`bg-center bg-cover bg-no-repeat rounded-[20px] shrink-0 ${classes.container}`} style={{ backgroundImage: `url('${imageUrl}')` }} aria-label={title} />
        ) : (
          <a href="#" onClick={(e)=>e.preventDefault()} className={`bg-center bg-cover bg-no-repeat rounded-[20px] shrink-0 ${classes.container}`} style={{ backgroundImage: `url('${imageUrl}')` }} aria-label={title} />
        )
      ) : (
        <div className={`bg-center bg-cover bg-no-repeat rounded-[20px] shrink-0 ${classes.container}`} style={{ backgroundImage: `url('${imageUrl}')` }} />
      )}
      <div className={`relative shrink-0 w-full`}>
        <div className="relative size-full">
          <div className={`box-border content-stretch flex flex-col ${classes.contentGap} items-start justify-start px-1 ${!isMobile ? 'pb-6' : 'pb-0'} pt-0 relative w-full h-full`}>
            <div className="content-stretch flex gap-2 items-start justify-start relative shrink-0 w-full">
              {keywords.map((keyword, i) => (
                <KeywordLabel key={i} text={keyword} />
              ))}
            </div>
            <div className={`content-stretch flex flex-col gap-3 items-center justify-start relative shrink-0 w-full`}>
              <div className={`content-stretch flex flex-col font-['Pretendard:Medium',_sans-serif] ${classes.titleDescGap} items-start justify-start leading-[0] not-italic relative shrink-0 w-full ${classes.cardHeight}`}>
                {href ? (
                  isAuthed === true ? (
                    <a href={href} className={`relative shrink-0 text-[#010103] ${classes.titleText} w-full ${classes.titleHeight} hover:underline`}>
                      <p className={`${isMobile ? 'leading-[1.4]' : 'leading-[1.5]'} line-clamp-2 overflow-hidden text-ellipsis break-keep font-bold`}>{title}</p>
                    </a>
                  ) : isAuthed === false ? (
                    <a href="#" onClick={onBlockedClick} className={`relative shrink-0 text-[#010103] ${classes.titleText} w-full ${classes.titleHeight}`}>
                      <p className={`${isMobile ? 'leading-[1.4]' : 'leading-[1.5]'} line-clamp-2 overflow-hidden text-ellipsis break-keep font-bold`}>{title}</p>
                    </a>
                  ) : (
                    <a href="#" onClick={(e)=>e.preventDefault()} className={`relative shrink-0 text-[#010103] ${classes.titleText} w-full ${classes.titleHeight}`}>
                      <p className={`${isMobile ? 'leading-[1.4]' : 'leading-[1.5]'} line-clamp-2 overflow-hidden text-ellipsis break-keep font-bold`}>{title}</p>
                    </a>
                  )
                ) : (
                  <div className={`relative shrink-0 text-[#010103] ${classes.titleText} w-full ${classes.titleHeight}`}>
                    <p className={`${isMobile ? 'leading-[1.4]' : 'leading-[1.5]'} line-clamp-2 overflow-hidden text-ellipsis break-keep font-bold`}>{title}</p>
                  </div>
                )}
                {size === 'large' && (
                  <div className={`-webkit-box css-ni1ftd overflow-ellipsis overflow-hidden relative shrink-0 text-[#65676e] text-lg lg:text-[18px] tracking-[-0.36px] w-full ${classes.descHeight}`}>
                    <p className={`leading-[1.5] ${isMobile ? 'line-clamp-2' : 'line-clamp-3'}`}>{description}</p>
                  </div>
                )}
                <div className={`overflow-ellipsis overflow-hidden relative shrink-0 text-[#65676e] text-base lg:text-lg text-nowrap tracking-[-0.32px] w-full ${isCuration && !isMobile ? 'mt-3' : size === 'large' ? 'mt-1' : 'mt-2 lg:mt-3'}`}>
                  <p className="[text-overflow:inherit] [text-wrap-mode:inherit] [white-space-collapse:inherit] leading-[normal] overflow-inherit">{date}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <LoginRequiredToast isVisible={toastVisible} onClose={() => setToastVisible(false)} />
    </div>
  );
}

